import BaseRegulation, {BaseRegulationParams, IShowLoginGamePopup} from "../base_regulation";
import TempAccountMessageRegulationHelper from "../../helpers/temp_account_message_helper";
import Event from "../../../../utils/event";
import {SYSTEM_EVENTS, CLIENT_EVENTS} from "../../../../events/events";
import _ from "../../../../utils/underscore";
import GenericRealityCheckComponent
    from "../../../../components/regulation_components/helpers/reality_check/generic_reality_check_component";
import {GNWP_SOURCES} from "../../../../components/external_links/external_links_consts";
import Store from "../../../../utils/store";
import {CyClass} from "../../../../../../common/cy-classes";
import GermanyResponsibleGamingIconsController
    from "../../../../components/regulation_components/germany/germany_responsible_gaming_buttons_controller";
import {GermanyResponsibleGamingButtonPlacement} from "../../../../../../common/const/responsible-gaming";
import {BUTTON_TYPE} from "../../../../components/message/message_consts";

interface GermanyRegulationParams extends BaseRegulationParams {
    tempAccountMessageHelper?: TempAccountMessageRegulationHelper,
    placement?:GermanyResponsibleGamingButtonPlacement
}

interface IAccountData {
    RegulationStatusId: number,
}

const RegulationStatusIdMap = {
    ApprovedForRegulation: 0,
    PendingDocuments: 2
};

interface IRealityCheckMessageConfig {
    title: string;
    text?: string;
    closeButton: boolean;
    buttons: [
        {
            name: string;
            isClosable: boolean;
            cssClass?: string;
            callback: () => void;
            type?: string;
        }
    ]
}
const IS_GAME_POPUP_APPROVED_STORAGE_KEY = "cgp_isGamePopupApproved";

export default class GermanyRegulation extends BaseRegulation {
    static CLASS = 'GermanyRegulation';
    _tempAccountMessageHelper: TempAccountMessageRegulationHelper;
    private _isGamePopupShown:boolean = false;

    init(params: GermanyRegulationParams) {
        super.init(params);
        this._tempAccountMessageHelper = params.tempAccountMessageHelper || TempAccountMessageRegulationHelper.create({
            serviceFactory: this.serviceFactory
        });

        this._subscribe();
    }

    public getTempAccountGnwpSource(): string{
        return GNWP_SOURCES.DOCUMENT_UPLOAD;
    }

    getResponsibleGamingIconsComponent(params) {
        return GermanyResponsibleGamingIconsController.create(params);
    }
    showResponsibleIcons() {
        return this.serviceFactory.oAuth2Service().isAuthorized();
    }

    realityCheckEnabled() {
        return true;
    }

    sessionTimerEnabled() {
        return true;
    }

    isAnonymousPlayAllowed() : boolean {
        if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return false;
        }

        return this._getAccountData().RegulationStatusId === RegulationStatusIdMap.ApprovedForRegulation;
    }

    showLoginMessage() {
        if (this._IsDocUploadRequired()) {
            return this.serviceFactory.openLinkService().openLink({
                    source: GNWP_SOURCES.DOCUMENT_UPLOAD
                }
            );
        }
    }

    postLoginActionsArePassed() {
        return this._isGamePopupApproved()
            ||
            (
                !this._isGamePopupShown
                &&
                !this._shouldShowGamePopup()
            );
    }

    private _IsDocUploadRequired(){
        const accountData = this.serviceFactory.userService().getAccountData();
        return accountData.IsDocUploadRequired;
    }

    private _shouldShowGamePopup(params?: IShowLoginGamePopup) {
        if(
            !this.serviceFactory.oAuth2Service().isAuthorized()
            ||
            this._IsDocUploadRequired()
            ||
            (params?.fromRegistration || this.serviceFactory.oAuth2Service().isLoginFromRegistration())
            ||
            this._isGamePopupApproved()
        ) {
            return false;
        }

        return true;
    }

    async showGamePopup(params?: IShowLoginGamePopup): Promise<void> {
        if(!this._shouldShowGamePopup(params)) {
            return;
        }

        let gameType: number | null = null;

        try {
            const recentlyPlayedGames = await this.serviceFactory.recentlyPlayedService().getRecentlyPlayedList();

            if (_.isEmpty(recentlyPlayedGames)) {
                const aiList = await this.serviceFactory.aiListService().getAIList(1, false);
                if (aiList && aiList.aiGameTypesArray && !_.isEmpty(aiList.aiGameTypesArray)) {
                    gameType = aiList.aiGameTypesArray[0];
                }
            } else {
                gameType = recentlyPlayedGames[0];
            }
        } catch (err) {
            this.logger.error('Failed to get game to popup from recently played or from AI recommendation', err);
        }

        // Default is Millionare Geneie
        if(!gameType) {
            gameType = 130043;
        }

        this._gamePopupShownCallback();
        Event.fire(SYSTEM_EVENTS.GAME_POPUP_SHOW, {
            gameType: gameType,
            gamePopupApprovedCallback: ()=> {this._userApproveGamePopupCallback()}
        });
    }

    /**
     * Overrides base class
     * @param sessionTimer
     * @private
     */
    _handleRealityCheckMessage(sessionTimer) {
        const continueGAObj = {
            category: 'reality_check_message',
            action: 'continue_in_lobby'
        };

        const languageService = this.serviceFactory.languageService();
        const messageConfig: IRealityCheckMessageConfig = {
            title: languageService.translation('reality_check_title'),
            closeButton: false,
            buttons: [{
                name: languageService.translation('reality_check_continue_btn'),
                cssClass: CyClass("cy-reality-check-close-button"),
                isClosable: true,
                callback: () => {
                    this.serviceFactory.gameService().continuePlayCurrentGame();
                    Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, continueGAObj);
                    Event.fire(CLIENT_EVENTS.REALITY_CHECK_CLOSED);
                }
            }]
        };

        // Add close game button
        if (this.serviceFactory.gameService().isGameRunning()) {
            if (!this._realityCheckShouldBeDisplayedInGame()) {
                return;
            }

            continueGAObj.action = 'continue_in_game';

            messageConfig.buttons.unshift({
                name: languageService.translation('close_game'),
                type: BUTTON_TYPE.SECONDARY.typeName,
                isClosable: true,
                callback: () => {
                    Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, {
                        category: 'reality_check_message',
                        action: 'close_game'
                    });
                    Event.fire(CLIENT_EVENTS.REALITY_CHECK_CLOSED);
                    this.serviceFactory.gameService().closeRunningGame();
                }
            });
        }

        this.serviceFactory
            .userService()
            .getRealityCheckData()
            .then(data => {
                Event.fire(CLIENT_EVENTS.REALITY_CHECK_OPENED);
                messageConfig.text = this._getRealityCheckMessageText(data);
                this._openRealityCheckMessage(messageConfig);
            })
            .catch(() => {
                messageConfig.text = this._getRealityCheckMessageText();
                this._openRealityCheckMessage(messageConfig);
            });
    }

    _subscribe() {
        Event.on(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._initGamePopup,this);
    }

    _realityCheckShouldBeDisplayedInGame() {
        return this.serviceFactory
            .gameService()
            .shouldShowRealityCheckMessageWhileGameIsOpen();
    }

    _openRealityCheckMessage(messageConfig) {
        messageConfig = {
            ...messageConfig,
            showPopupAboveSecondWebViewHybrid: true
        };

        this.serviceFactory.gameService().stopCurrentGameAutoplay();
        this.serviceFactory.messageManagerService().openMessage(messageConfig);
    }


    /**
     * Returns the text displayed in the reality check message according to the received (or not) reality check data
     * @param realityCheckData
     * @returns {String}
     * @private
     */
    _getRealityCheckMessageText(realityCheckData?) {
        let componentConfig,
            component;


        if (_.isNullOrUndefined(realityCheckData)) {
            componentConfig = {
                elapsedTime: this.getCurrentSessionTime(),
                sessionBalance: 0
            }
        } else {
            componentConfig = {
                elapsedTime: this.getCurrentSessionTime(),
                sessionBalance: -1 * realityCheckData.TotalNetLoss
            }
        }

        component = GenericRealityCheckComponent.create(componentConfig);
        component.start();

        return component.getEl();
    }

    shouldHaveBetLimitsPerBrand(): boolean {
        return true;
    }

    isSessionCooldownEnabled(): boolean {
        return true;
    }

    private _getAccountData(): IAccountData {
        return this.serviceFactory.userService().getAccountData() as IAccountData;
    }

    private _isGamePopupApproved(): boolean {
        return Store.get(IS_GAME_POPUP_APPROVED_STORAGE_KEY) || false;
    }

    _gamePopupShownCallback() {
        this._isGamePopupShown = true;
    }

    _initGamePopup(): void {
        if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
            this._isGamePopupShown = false;
            Store.del(IS_GAME_POPUP_APPROVED_STORAGE_KEY);
        }
    }

    _userApproveGamePopupCallback(): void {
        Store.set(IS_GAME_POPUP_APPROVED_STORAGE_KEY, true);

        Event.fire(SYSTEM_EVENTS.USER_APPROVE_GAME_POPUP);
    }

    dispose() {
        Event.off(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._initGamePopup,this);
    }
}